const EMPTY_RECOMMENDATION_VALUES = new Set([
    '',
    '-',
    '—',
    'null',
    'undefined',
    'none',
    'n/a',
    'na',
]);

const RECOMMENDATION_ORDER = ['primary', 'drafting_target', 'alternative'];

const LABEL_TO_KEY_RE = {
    primary: /(?:action|prioritaire|principale|primary|priority|levier)/i,
    drafting_target: /(?:r[eé]daction|redaction|drafting|wording|texte cible|target|formulation)/i,
    alternative: /(?:alternative|secondaire|fallback|repli)/i,
};

const SECTION_LABEL_RE = /(?:^|\n+)\s*(?:[-*]\s*)?(?:\*\*)?\s*(Levier prioritaire à examiner|Levier prioritaire a examiner|Action prioritaire|Action recommand[eé]e|Option principale|Version principale|Priority lever to examine|Primary action|Priority action|Exemple de formulation à valider|Exemple de formulation a valider|R[eé]daction cible|Redaction cible|Texte cible|Wording example to validate|Drafting target|Target wording|Option alternative à examiner|Option alternative a examiner|Alternative option to review|Option alternative|Option secondaire|Fallback option|Alternative)\s*(?:(?::|：|-|–|—)\s*)?(?:\*\*)?\s*/giu;

function cleanRecommendationText(value) {
    if (value === null || value === undefined) return '';
    const text = String(value)
        .replace(/\r\n?/g, '\n')
        .replace(/[ \t]+\n/g, '\n')
        .replace(/\n[ \t]+/g, '\n')
        .trim();

    return EMPTY_RECOMMENDATION_VALUES.has(text.toLowerCase()) ? '' : text;
}

function firstCleanValue(source, keys) {
    for (const key of keys) {
        const value = cleanRecommendationText(source?.[key]);
        if (value) return value;
    }
    return '';
}

function normalizeLabelKey(label) {
    const normalized = String(label || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase();

    if (LABEL_TO_KEY_RE.drafting_target.test(normalized)) return 'drafting_target';
    if (LABEL_TO_KEY_RE.alternative.test(normalized)) return 'alternative';
    return 'primary';
}

function mergeSection(target, key, value) {
    const clean = cleanRecommendationText(value);
    if (!clean) return;
    target[key] = target[key] ? `${target[key]}\n\n${clean}` : clean;
}

function parseLabeledRecommendationText(text) {
    const matches = [...text.matchAll(SECTION_LABEL_RE)];
    if (!matches.length) return null;

    const sections = {};
    matches.forEach((match, index) => {
        const key = normalizeLabelKey(match[1]);
        const start = match.index + match[0].length;
        const end = matches[index + 1]?.index ?? text.length;
        mergeSection(sections, key, text.slice(start, end));
    });

    return Object.keys(sections).length ? sections : null;
}

function splitRecommendationParagraphs(text) {
    const byParagraph = text
        .split(/\n{2,}/)
        .map(cleanRecommendationText)
        .filter(Boolean);

    if (byParagraph.length > 1) return byParagraph;

    return text
        .split(/\n+(?=\s*(?:["«A-ZÀ-ÖØ-Þ]|A defaut|À défaut|Prevoir|Prévoir))/u)
        .map(cleanRecommendationText)
        .filter(Boolean);
}

function inferParagraphKey(paragraph, index, total) {
    if (/^(?:A defaut|À défaut|En alternative|Alternative\b|Option alternative\b|Prevoir\b|Prévoir\b)/i.test(paragraph)) {
        return 'alternative';
    }

    if (
        /^(?:["«]|La clause|Le contrat)/i.test(paragraph)
        || /\b(?:s'engage|est remplacee? par|est remplac[ée]e? par|remplacer|inserer|ins[eé]rer|ajouter|stipuler|libellee?|libell[ée]e?|formulation|rediger|r[eé]diger)\b/i.test(paragraph)
    ) {
        return 'drafting_target';
    }

    if (index === 0) return 'primary';
    if (total >= 3 && index === 1) return 'drafting_target';
    return 'alternative';
}

function parsePlainRecommendationText(text) {
    const paragraphs = splitRecommendationParagraphs(text);
    if (!paragraphs.length) return {};

    if (paragraphs.length === 1) {
        return { primary: paragraphs[0] };
    }

    const sections = {};
    paragraphs.forEach((paragraph, index) => {
        mergeSection(sections, inferParagraphKey(paragraph, index, paragraphs.length), paragraph);
    });
    return sections;
}

function optionsFromSections(sections) {
    return RECOMMENDATION_ORDER
        .map((key) => {
            const text = cleanRecommendationText(sections?.[key]);
            return text ? { key, text } : null;
        })
        .filter(Boolean);
}

export function getRecommendationSections(recommendation) {
    if (!recommendation) return [];

    if (typeof recommendation === 'object') {
        const sections = {
            primary: firstCleanValue(recommendation, ['primary', 'primary_action', 'priority_action', 'action']),
            drafting_target: firstCleanValue(recommendation, [
                'drafting_target',
                'draftingTarget',
                'target_wording',
                'wording',
                'redaction_cible',
                'redactionTarget',
            ]),
            alternative: firstCleanValue(recommendation, ['alternative', 'alternative_version', 'fallback', 'option']),
        };

        if (sections.primary && !sections.drafting_target && !sections.alternative) {
            const parsedPrimary = parseLabeledRecommendationText(sections.primary)
                || parsePlainRecommendationText(sections.primary);
            const parsedOptions = optionsFromSections(parsedPrimary);
            if (parsedOptions.length > 1) return parsedOptions;
        }

        return optionsFromSections(sections);
    }

    const text = cleanRecommendationText(recommendation);
    if (!text) return [];

    return optionsFromSections(
        parseLabeledRecommendationText(text) || parsePlainRecommendationText(text)
    );
}

export function getRecommendationText(recommendation) {
    return getRecommendationSections(recommendation)
        .map((section) => section.text)
        .join('\n');
}
